import { Modal } from "components";
import { ButtonWhite } from "components/Atoms/Button";
import RequiredNumberInput from "components/ValidatedFields/RequiredNumberInput";
import ValidatorContext from "formvalidation/FormContext";
import { useValidators } from "formvalidation/hooks";
import { CreditApi } from "queries/Credit";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { formatCurrency } from "utilities/currencyutils";
import React from "react";
import { useClientId } from "common/SessionContext";

interface Props {
  onClose: () => void;
}

export const BuyCreditsModal = (props: Props) => {
  if (!site.permissions.canConfigureSystemSettings) {
    return <Modal
      isOpen
      handleClose={props.onClose}
      title={RESX.Credit.AddCredits}
      width="tiny"
    >
      <div className="ui message red">You need to have system admin permission.</div>
    </Modal>;
  }

  return <BuyCreditsForm {...props} />;
};

const BuyCreditsForm = (props: Props) => {
  const clientId = useClientId();
  const min = serverReference.constants.credit.minPurchaseAmount;
  const max = serverReference.constants.credit.maxPurchaseAmount;
  const defaultAmount = serverReference.constants.credit.defaultPurchaseAmount;

  const [amount, setAmount] = React.useState<number>(defaultAmount);

  const validators = useValidators();
  const queryClient = useQueryClient();

  const purchaseMutation = useMutation((dto: server.dto.PurchaseCreditsInstruction) =>
    window.CoreApi.Credit.PurchaseCredits(dto, { toastOnError: true })
  );

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const validatorResult = await validators.validateAll();
    if (validatorResult.hasErrors) {
      return;
    }

    const res = await purchaseMutation.mutateAsync({
      clientId,
      amount,
    });

    if (!res.data.hasErrors) {
      queryClient.invalidateQueries(CreditApi.GetCreditAccountByClient.getCacheKey({ clientId }));
      props.onClose();
    }
  };

  return <Modal
    isOpen
    handleClose={props.onClose}
    title={RESX.Credit.AddCredits}
    width="tiny"
  >
    <ValidatorContext settings={validators.validatorSettings}>
      <form className="flex-column gap-big" onSubmit={onSubmit}>
        <div className="flex-column gap">
          <div>
            Enter an amount between{" "}
            <strong>{formatCurrency(min, "eur", serverReference.culture.uiLocale, { hideCents: true })}</strong>
            {" "}and{" "}
            <strong>{formatCurrency(max, "eur", serverReference.culture.uiLocale, { hideCents: true })}</strong>.
          </div>

          <div>
            <RequiredNumberInput
              value={amount}
              onChange={setAmount}
              minValue={min}
              maxValue={max}
              decimalPoints={0}
              canBeEmpty={false}
              canBeNegative={false}
              validatorKey="amount"
              labelContent="EUR"
              width={100}
            />
          </div>
        </div>
        <div className="flex gap-big align-center">
          <small className="grow1">
            Your credit balance will be consumed with AI data import and Peppol usage and expire 1 year after purchase.
            By clicking &quot;Buy credits&quot; you agree to our Terms &amp; Conditions.
          </small>
          <ButtonWhite
            htmlType="submit"
            loading={purchaseMutation.isLoading}
            disabled={purchaseMutation.isLoading}
            label={RESX.Credit.BuyCredits}
          />
        </div>
      </form>
    </ValidatorContext>
  </Modal>;
};
